import {
  connectInstagram,
  connectFacebook,
  connectTwitter,
  connectLinkedIn,
  connectTikTok,
  connectYouTube,
  connectPinterest
} from '../firebase/oauthService';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import React, { useState, useEffect } from 'react';
import { X, User, LogOut, Unlink, Plus, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { logoutUser } from '../firebase/authService';
import { getAllPlatforms, deletePlatform } from '../firebase/platformService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SettingsModal = ({ isOpen, onClose, onPlatformsUpdate }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('platforms');
  const [userPlatforms, setUserPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showPlatformSelector, setShowPlatformSelector] = useState(false);
  const [selectedNewPlatforms, setSelectedNewPlatforms] = useState([]);

  // Tüm mevcut sosyal medya platformları
  const allAvailablePlatforms = [
    { id: 'instagram', name: 'Instagram', icon: '📷', logo: '/logos/instagram.png', color: '#E4405F' },
    { id: 'facebook', name: 'Facebook', icon: '👍', logo: '/logos/facebook.png', color: '#1877F2' },
    { id: 'twitter', name: 'Twitter (X)', icon: '🐦', logo: '/logos/twitter.png', color: '#1DA1F2' },
    { id: 'linkedin', name: 'LinkedIn', icon: '💼', logo: '/logos/linkedin.png', color: '#0A66C2' },
    { id: 'tiktok', name: 'TikTok', icon: '🎵', logo: '/logos/tiktok.png', color: '#000000' },
    { id: 'youtube', name: 'YouTube', icon: '▶️', logo: '/logos/youtube.png', color: '#FF0000' },
    { id: 'pinterest', name: 'Pinterest', icon: '📌', logo: '/logos/pinterest.png', color: '#E60023' },
    { id: 'snapchat', name: 'Snapchat', icon: '👻', logo: '/logos/snapchat.png', color: '#FFFC00' },
    { id: 'reddit', name: 'Reddit', icon: '🤖', logo: '/logos/reddit.png', color: '#FF4500' },
    { id: 'tumblr', name: 'Tumblr', icon: '📝', logo: '/logos/tumblr.png', color: '#35465C' },
    { id: 'whatsapp', name: 'WhatsApp Business', icon: '💬', logo: '/logos/whatsapp.png', color: '#25D366' },
    { id: 'telegram', name: 'Telegram', icon: '✈️', logo: '/logos/telegram.png', color: '#0088cc' },
    { id: 'discord', name: 'Discord', icon: '🎮', logo: '/logos/discord.png', color: '#5865F2' },
    { id: 'threads', name: 'Threads', icon: '🧵', logo: '/logos/threads.png', color: '#000000' },
    { id: 'mastodon', name: 'Mastodon', icon: '🐘', logo: '/logos/mastodon.png', color: '#6364FF' },
    { id: 'bluesky', name: 'Bluesky', icon: '🦋', logo: '/logos/bluesky.png', color: '#1185FE' }
  ];

  useEffect(() => {
    if (isOpen && currentUser) {
      loadPlatforms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUser]);

 const loadPlatforms = async () => {
  const result = await getAllPlatforms(currentUser.uid);
  if (result.success) {
    // Tüm platformları göster (enabled true/false farketmez)
    setUserPlatforms(result.data);
    // Ana sayfaya TÜM eklenen platformları gönder (bağlı ve bağlı olmayan)
    if (onPlatformsUpdate) {
      onPlatformsUpdate(result.data);
    }
  }
};

  const handleSelectPlatform = (platformId) => {
    if (selectedNewPlatforms.includes(platformId)) {
      setSelectedNewPlatforms(selectedNewPlatforms.filter(id => id !== platformId));
    } else {
      setSelectedNewPlatforms([...selectedNewPlatforms, platformId]);
    }
  };

  const handleAddPlatforms = async () => {
  if (selectedNewPlatforms.length === 0) {
    alert(t('settings.platforms.messages.selectOne'));
    return;
  }

  setLoading(true);

  // Her seçili platformu enabled=false olarak kaydet (henüz bağlı değil)
  for (const platformId of selectedNewPlatforms) {
    const platformInfo = allAvailablePlatforms.find(p => p.id === platformId);
    await setDoc(doc(db, 'users', currentUser.uid, 'platforms', platformId), {
      enabled: false, // Henüz bağlı değil
      token: null,
      username: null,
      name: platformInfo?.name || platformId,
      addedAt: new Date().toISOString()
    });
  }

  setLoading(false);
  setShowPlatformSelector(false);
  setSelectedNewPlatforms([]);

  // Platformları yeniden yükle
  await loadPlatforms();

  setSaveMessage(t('settings.platforms.messages.added'));
  setTimeout(() => setSaveMessage(''), 3000);
};

  const handleDisconnect = async (platformId, platformName) => {
    if (!window.confirm(t('settings.platforms.messages.confirmDelete', { platform: platformName }))) {
      return;
    }

    setLoading(true);
    const result = await deletePlatform(currentUser.uid, platformId);

    if (result.success) {
      setSaveMessage(t('settings.platforms.messages.deleted', { platform: platformName }));
      await loadPlatforms();
      setTimeout(() => setSaveMessage(''), 3000);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    if (window.confirm(t('settings.profile.confirmLogout'))) {
      await logoutUser();
      navigate('/login');
    }
  };

  if (!isOpen) return null;

  // Kullanıcının henüz eklemediği platformlar
  const availableToAdd = allAvailablePlatforms.filter(
    p => !userPlatforms.some(up => up.id === p.id)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">{t('settings.title')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b-2 border-gray-200 px-6">
          <button
            onClick={() => setActiveTab('platforms')}
            className={`px-6 py-3 font-semibold transition ${
              activeTab === 'platforms'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {t('settings.tabs.platforms')}
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-semibold transition ${
              activeTab === 'profile'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {t('settings.tabs.profile')}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Success Message */}
          {saveMessage && (
            <div className="mb-4 bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-lg">
              ✅ {saveMessage}
            </div>
          )}

          {/* Platforms Tab */}
          {activeTab === 'platforms' && (
            <div className="space-y-4">
              
              {/* Add Platform Button */}
              {!showPlatformSelector && (
                <button
                  onClick={() => setShowPlatformSelector(true)}
                  className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-blue-400 rounded-xl text-blue-600 hover:bg-blue-50 transition font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  {t('settings.platforms.addNew')}
                </button>
              )}

              {/* Platform Selector */}
              {showPlatformSelector && (
                <div className="border-2 border-blue-400 rounded-xl p-6 bg-blue-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800">{t('settings.platforms.selectPlatform')}</h3>
                    <button
                      onClick={() => {
                        setShowPlatformSelector(false);
                        setSelectedNewPlatforms([]);
                      }}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    {availableToAdd.map(platform => (
                      <button
                        key={platform.id}
                        onClick={() => handleSelectPlatform(platform.id)}
                        className={`p-4 rounded-xl border-2 transition ${
                          selectedNewPlatforms.includes(platform.id)
                            ? 'border-blue-500 bg-blue-100'
                            : 'border-gray-300 bg-white hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 flex items-center justify-center">
                            <img
                              src={platform.logo}
                              alt={platform.name}
                              className="w-7 h-7 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <span className="text-2xl hidden">{platform.icon}</span>
                          </div>
                          <span className="text-sm font-semibold">{platform.name}</span>
                          {selectedNewPlatforms.includes(platform.id) && (
                            <Check className="w-4 h-4 text-blue-600 ml-auto" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {availableToAdd.length === 0 && (
                    <p className="text-center text-gray-600 py-4">
                      {t('settings.platforms.allAdded')}
                    </p>
                  )}

                  <button
                    onClick={handleAddPlatforms}
                    disabled={selectedNewPlatforms.length === 0}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('settings.platforms.addSelected', { count: selectedNewPlatforms.length })}
                  </button>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>{t('settings.platforms.infoBox.title')}</strong><br/>
                  {t('settings.platforms.infoBox.description')}
                </p>
              </div>

              {/* Connected Platforms */}
              {userPlatforms.length === 0 && !showPlatformSelector && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔗</div>
                  <p className="text-gray-600">{t('settings.platforms.noAdded')}</p>
                  <p className="text-sm text-gray-500 mt-2">{t('settings.platforms.clickToAdd')}</p>
                </div>
              )}

              {userPlatforms.map(platform => {
                const platformInfo = allAvailablePlatforms.find(p => p.id === platform.id);

                return (
                  <div key={platform.id} className="border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 flex items-center justify-center">
                          <img
                            src={platformInfo?.logo}
                            alt={platformInfo?.name || platform.id}
                            className="w-10 h-10 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <span className="text-3xl hidden">{platformInfo?.icon || '🌐'}</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">
                            {platformInfo?.name || platform.id}
                          </h3>
                          {platform.token ? (
  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
    {t('settings.platforms.connected')}
  </span>
) : (
  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
    {t('settings.platforms.notConnected')}
  </span>
)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDisconnect(platform.id, platformInfo?.name || platform.id)}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-semibold"
                      >
                        <Unlink className="w-4 h-4" />
                        {t('settings.platforms.delete')}
                      </button>
                    </div>

                    {platform.token ? (
                      // Bağlı durumu
                      <div className="space-y-3">
                        <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-green-700">{t('settings.platforms.successfullyConnected')}</p>
                              {platform.username && (
                                <p className="text-xs text-green-600 mt-1">@{platform.username}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(platform.connectedAt).toLocaleDateString('tr-TR')}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDisconnect(platform.id, platformInfo?.name || platform.id)}
                              className="text-red-600 hover:text-red-700 text-sm font-semibold"
                            >
                              {t('settings.platforms.disconnect')}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Bağlı değil durumu - OAuth popup butonu
                      <div className="space-y-3">
                        <button
                          onClick={async () => {
                            setLoading(true);
                            try {
                              switch (platform.id) {
                                case 'instagram':
                                  connectInstagram(currentUser.uid);
                                  break;
                                case 'facebook':
                                  connectFacebook(currentUser.uid);
                                  break;
                                case 'twitter':
                                  await connectTwitter(currentUser.uid);
                                  break;
                                case 'linkedin':
                                  connectLinkedIn(currentUser.uid);
                                  break;
                                case 'tiktok':
                                  connectTikTok(currentUser.uid);
                                  break;
                                case 'youtube':
                                  connectYouTube(currentUser.uid);
                                  break;
                                case 'pinterest':
                                  connectPinterest(currentUser.uid);
                                  break;
                                default:
                                  alert(t('settings.platforms.comingSoon', { platform: platformInfo?.name || platform.id }));
                                  setLoading(false);
                              }
                            } catch (error) {
                              alert(t('settings.platforms.messages.connectionError', { error: error.message }));
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg hover:shadow-lg transition font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                          </svg>
                          {loading ? t('settings.platforms.openingPopup') : t('settings.platforms.connect', { platform: platformInfo?.name || platform.id })}
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          {t('settings.platforms.secureOAuth')}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl">
                    {currentUser?.photoURL ? (
                      <img src={currentUser.photoURL} alt="Profile" className="w-full h-full rounded-full" />
                    ) : (
                      <User className="w-8 h-8 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{currentUser?.displayName || t('settings.profile.user')}</h3>
                    <p className="text-blue-100">{currentUser?.email}</p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{t('settings.profile.accountInfo')}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('settings.profile.email')}</span>
                    <span className="font-semibold">{currentUser?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('settings.profile.connectedPlatforms')}</span>
                    <span className="font-semibold">{t('settings.profile.platformsCount', { count: userPlatforms.length })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('settings.profile.emailVerified')}</span>
                    <span className={currentUser?.emailVerified ? 'text-green-600' : 'text-orange-600'}>
                      {currentUser?.emailVerified ? t('settings.profile.yes') : t('settings.profile.no')}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white py-3 rounded-xl hover:bg-red-700 transition font-semibold"
              >
                <LogOut className="w-5 h-5" />
                {t('settings.profile.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;